/**
 * @zendir/ui — Astro UXDS status palette (primitive)
 *
 * Operator-grade severity ramps from the Astro UX Design System. Stops
 * 50–300 and 950 are omitted — genuinely undefined (TBD) in the source
 * Figma file for every status color, not values we failed to capture.
 *
 * The canonical single-value status tokens map onto these ramps:
 *   dark status    → 500 (standby → cyan 600)
 *   light fill     → 600 (standby → cyan 500)
 *   light border   → 900
 *
 * @see https://www.astrouxds.com/patterns/status-system/
 */

export const red = {
  400: "#FF5F60",
  500: "#FF3838",
  600: "#FF2A04",
  700: "#C8102E",
  800: "#8B1703",
  900: "#661102",
} as const;

export const amber = {
  400: "#FFCC57",
  500: "#FFB302",
  600: "#FFAF3D",
  700: "#FF8C00",
  800: "#975F0E",
  900: "#664618",
} as const;

export const yellow = {
  400: "#FDED61",
  500: "#FCE83A",
  600: "#FAD800",
  700: "#C7AB00",
  800: "#917D01",
  900: "#645600",
} as const;

export const green = {
  400: "#99F666",
  500: "#56F000",
  600: "#00E200",
  700: "#00AD23",
  800: "#007A33",
  900: "#005A00",
} as const;

export const cyan = {
  400: "#5CE2FF",
  500: "#64D9FF",
  600: "#2DCCFF",
  700: "#20A9D5",
  800: "#35798E",
  900: "#285766",
} as const;

/** "Off" has no ramp in Astro UXDS — single canonical value. */
export const off = "#A4ABB6" as const;

/**
 * Canonical Astro status tokens. Every theme's `colors.status` block must
 * use these — severity colors are immutable across themes.
 */
export const astroStatus = {
  critical: red[500],
  serious: amber[500],
  caution: yellow[500],
  normal: green[500],
  standby: cyan[600],
  off,
} as const;

/** Light-mode status fills (Astro light theme guidance). */
export const astroStatusLightFill = {
  critical: red[600],
  serious: amber[600],
  caution: yellow[600],
  normal: green[600],
  standby: cyan[500],
  off: "#7B8089",
} as const;

/** Light-mode status borders (Astro light theme guidance). */
export const astroStatusLightBorder = {
  critical: red[900],
  serious: amber[900],
  caution: yellow[900],
  normal: green[900],
  standby: cyan[900],
  off: "#3C3E42",
} as const;

/** All Astro ramps keyed by display name (drives Foundation/Palette). */
export const astroScales = {
  "Red — Critical": red,
  "Amber — Serious": amber,
  "Yellow — Caution": yellow,
  "Green — Normal": green,
  "Cyan — Standby": cyan,
} as const;
